import { useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Box, Flex, Grid } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { useSelector } from 'react-redux';

import { ServiceConstants } from 'constant';
import { UserResponse, UserSimpleResponse } from 'types';

import { IRootState } from 'config/redux';
import Modal from './base';
import Button from '../button/standard';
import { TFunction } from 'i18next';
import { AppUtils } from 'utils';
import { AsyncAutocomplete, Title } from 'components';
import { UserService } from 'services';

const { REQUEST_PENDING, REQUEST_USER_AGENTS_LIST_ID } = ServiceConstants;

interface AgentChangeModalProps {
  opened: boolean;
  onClose: (val: boolean) => void;
  onSubmit: (agent: UserSimpleResponse) => void;
  afterSubmit: (val: UserSimpleResponse) => void;
  requestId: string;
  t: TFunction;
  agent: UserSimpleResponse;
}

const AgentChangeModal = ({ opened, onClose, onSubmit, afterSubmit, requestId, t, agent }: AgentChangeModalProps) => {

  const serviceObject = useSelector((state: IRootState) => state.service.object[requestId])
  const [newAgent, setNewAgent] = useState<UserSimpleResponse | undefined>(undefined);

  useEffect(() => {
    if (!opened) {
      return;
    }
    AppUtils.resolveServiceObject(serviceObject, requestId, (val) => {
      setNewAgent(val);
      afterSubmit(val);
      onClose(false);
  });
  }, [serviceObject])

  const isLoading = useMemo(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject]);
  const onSubmitWrapper = useCallback(() => {
    if (!newAgent) return;
    onSubmit(newAgent);
  }, [newAgent]);

  const renderAvatar = useCallback((user: UserSimpleResponse) => {
    if (user.profileImage) {
      const imageUrl = `${process.env.REACT_APP_API_URL_ATTACHMENTS}/${user.profileImage}`;
      return <Avatar size={30} mt={8} src={imageUrl} />
    }
    return <Avatar size={30} mt={8} />;
  }, []);

  const getAgents = useCallback((search: string | undefined) => {
    UserService.usersAgentsAll({ search, page: 0 })
  }, [])

  return (
    <Modal opened={opened} onClose={() => onClose(false)} title={t('agent.title')} size={'lg'}>
      <Grid>
        <Grid.Col span={5}>
          <Box>
            <Flex align={'start'}>
              {renderAvatar(agent)}
              <Title ml={'sm'} order={5}>{agent.fullName}</Title>
            </Flex>
          </Box>
        </Grid.Col>
        <Grid.Col span={2} mt={12}>
          <IconArrowRight />
        </Grid.Col>
        <Grid.Col span={5} mt={8}>
          <AsyncAutocomplete
            getData={getAgents}
            serviceObjectId={REQUEST_USER_AGENTS_LIST_ID}
            required={true}
            placeholder={'agent.placeholder'}
            pageable={true}
            renderName={(item: UserResponse) => item.fullName}
            key={'agent-autocomplete'}
            onChange={(item: any) => setNewAgent(item)}
          />
          <Box mt={20}>
            <Button
              fullWidth={true}
              disabled={isLoading || !newAgent}
              loading={isLoading}
              onClick={onSubmitWrapper}
            >
              {t('action.change')}
            </Button>
          </Box>
        </Grid.Col>
      </Grid>
    </Modal>
  )
}

export default AgentChangeModal;